import React, { useEffect, useState } from 'react';
import { Table, Tag, Button, Space, Modal, Descriptions, Card, Row, Col, Statistic } from 'antd';
import { FormattedMessage } from 'react-intl';
import type { ColumnsType } from 'antd/es/table';
import { EyeOutlined, RetweetOutlined } from '@ant-design/icons';
import { getEmailsApi } from '../api/apiInstances';
import AccessControl from '../components/common/AccessControl';
import { accessibilityRoles } from '../utils/accessUtils';
import logger from "../utils/logger";

interface OutboundEmail {
    id: string;
    idempotencyKey: string;
    status: 'queued' | 'sending' | 'sent' | 'failed' | 'dead';
    toEmail: string;
    toName?: string;
    subject: string;
    bodyHtml?: string;
    bodyText?: string;
    createdAt: string;
    updatedAt: string;
    availableAt: string;
    attemptCount: number;
    maxAttempts: number;
    lastErrorCode?: string;
    lastErrorMessage?: string;
    providerMessageId?: string;
}

interface EmailQueueStats {
    total: number;
    queued: number;
    sending: number;
    sent: number;
    failed: number;
    dead: number;
}

const EmailQueue: React.FC = () => {
    const [emails, setEmails] = useState<OutboundEmail[]>([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState<EmailQueueStats | null>(null);
    const [selectedEmail, setSelectedEmail] = useState<OutboundEmail | null>(null);
    const [detailsVisible, setDetailsVisible] = useState(false);

    const emailsApi = getEmailsApi();

    useEffect(() => {
        loadEmails();
        loadStats();
    }, []);

    const loadEmails = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await emailsApi.listEmails(
                undefined,
                100,
                0,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );
            setEmails(response.data.emails as any);
        } catch (error) {
            logger.error('Failed to load emails:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadStats = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await emailsApi.getQueueStats({
                headers: { Authorization: `Bearer ${token}` }
            });
            setStats(response.data as any);
        } catch (error) {
            logger.error('Failed to load stats:', error);
        }
    };

    const handleRequeue = async (emailId: string) => {
        try {
            const token = localStorage.getItem('token');
            await emailsApi.requeueEmail(emailId, {
                headers: { Authorization: `Bearer ${token}` }
            });
            Modal.success({
                title: 'Success',
                content: 'Email requeued successfully'
            });
            loadEmails();
            loadStats();
        } catch (error: any) {
            Modal.error({
                title: 'Error',
                content: error.response?.data?.detail || 'Failed to requeue email'
            });
        }
    };

    const showDetails = async (emailId: string) => {
        try {
            const token = localStorage.getItem('token');
            const response = await emailsApi.getEmail(emailId, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSelectedEmail(response.data as any);
            setDetailsVisible(true);
        } catch (error) {
            logger.error('Failed to load email details:', error);
        }
    };

    const getStatusTag = (status: string) => {
        const colorMap: Record<string, string> = {
            queued: 'blue',
            sending: 'cyan',
            sent: 'green',
            failed: 'orange',
            dead: 'red'
        };
        return <Tag color={colorMap[status] || 'default'}>{status.toUpperCase()}</Tag>;
    };


    return (
        <AccessControl accessibleFor={[accessibilityRoles.PLATFORM_ADMINISTRATOR]}>
            <Card
                title={<FormattedMessage id="email_queue" />}
                bordered={false}
            >
                {stats && (
                    <Row gutter={16} style={{ marginBottom: 24, paddingBottom: 16 }}>
                        <Col span={4}>
                            <Statistic title="Total" value={stats.total} />
                        </Col>
                        <Col span={4}>
                            <Statistic
                                title="Queued"
                                value={stats.queued}
                                valueStyle={{ color: '#1890ff' }}
                            />
                        </Col>
                        <Col span={4}>
                            <Statistic
                                title="Sending"
                                value={stats.sending}
                                valueStyle={{ color: '#13c2c2' }}
                            />
                        </Col>
                        <Col span={4}>
                            <Statistic
                                title="Sent"
                                value={stats.sent}
                                valueStyle={{ color: '#52c41a' }}
                            />
                        </Col>
                        <Col span={4}>
                            <Statistic
                                title="Failed"
                                value={stats.failed}
                                valueStyle={{ color: '#fa8c16' }}
                            />
                        </Col>
                        <Col span={4}>
                            <Statistic
                                title="Dead"
                                value={stats.dead}
                                valueStyle={{ color: '#f5222d' }}
                            />
                        </Col>
                    </Row>
                )}

                <Table
                    dataSource={emails}
                    loading={loading}
                    rowKey="id"
                    pagination={{ pageSize: 50 }}
                    className="dentobook-table"
                >
                    <Table.Column
                        key="status"
                        dataIndex="status"
                        title={<FormattedMessage id="status" defaultMessage="Status" />}
                        render={(status) => getStatusTag(status)}
                        sorter={(a: any, b: any) => (a.status || '').localeCompare(b.status || '')}
                    />
                    <Table.Column
                        key="toEmail"
                        dataIndex="toEmail"
                        title={<FormattedMessage id="to" defaultMessage="To" />}
                        render={(email, record: any) => (
                            <div>
                                {record.toName && <div><strong>{record.toName}</strong></div>}
                                <div style={{ color: '#888' }}>{email}</div>
                            </div>
                        )}
                        sorter={(a: any, b: any) => (a.toEmail || '').localeCompare(b.toEmail || '')}
                    />
                    <Table.Column
                        key="subject"
                        dataIndex="subject"
                        title={<FormattedMessage id="subject" defaultMessage="Subject" />}
                        ellipsis={true}
                        sorter={(a: any, b: any) => (a.subject || '').localeCompare(b.subject || '')}
                    />
                    <Table.Column
                        key="createdAt"
                        dataIndex="createdAt"
                        title={<FormattedMessage id="created" defaultMessage="Created" />}
                        render={(date) => new Date(date).toLocaleString()}
                        sorter={(a: any, b: any) => {
                            const dateA = new Date(a.createdAt).getTime();
                            const dateB = new Date(b.createdAt).getTime();
                            return dateA - dateB;
                        }}
                    />
                    <Table.Column
                        key="attemptCount"
                        dataIndex="attemptCount"
                        title={<FormattedMessage id="attempts" defaultMessage="Attempts" />}
                        render={(count, record: any) => `${count}/${record.maxAttempts}`}
                        sorter={(a: any, b: any) => a.attemptCount - b.attemptCount}
                    />
                    <Table.Column
                        key="actions"
                        title={<FormattedMessage id="actions" defaultMessage="Actions" />}
                        render={(_, record: any) => (
                            <Space>
                                <Button
                                    icon={<EyeOutlined />}
                                    size="small"
                                    onClick={() => showDetails(record.id)}
                                >
                                    <FormattedMessage id="details" defaultMessage="Details" />
                                </Button>
                                {(record.status === 'failed' || record.status === 'dead') && (
                                    <Button
                                        icon={<RetweetOutlined />}
                                        size="small"
                                        onClick={() => handleRequeue(record.id)}
                                    >
                                        <FormattedMessage id="requeue" defaultMessage="Requeue" />
                                    </Button>
                                )}
                            </Space>
                        )}
                    />
                </Table>
            </Card>

            {/* Details Modal */}
            <Modal
                title="Email Details"
                open={detailsVisible}
                onCancel={() => setDetailsVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setDetailsVisible(false)}>
                        Close
                    </Button>,
                    selectedEmail && (selectedEmail.status === 'failed' || selectedEmail.status === 'dead') && (
                        <Button
                            key="requeue"
                            type="primary"
                            icon={<RetweetOutlined />}
                            onClick={() => {
                                if (selectedEmail) {
                                    handleRequeue(selectedEmail.id);
                                    setDetailsVisible(false);
                                }
                            }}
                        >
                            Requeue
                        </Button>
                    )
                ]}
                width={800}
            >
                {selectedEmail && (
                    <>
                        <Descriptions bordered column={2} size="small">
                            <Descriptions.Item label="Status" span={2}>
                                {getStatusTag(selectedEmail.status)}
                            </Descriptions.Item>
                            <Descriptions.Item label="To Email">{selectedEmail.toEmail}</Descriptions.Item>
                            <Descriptions.Item label="To Name">{selectedEmail.toName || 'N/A'}</Descriptions.Item>
                            <Descriptions.Item label="Subject" span={2}>{selectedEmail.subject}</Descriptions.Item>
                            <Descriptions.Item label="Created At">{new Date(selectedEmail.createdAt).toLocaleString()}</Descriptions.Item>
                            <Descriptions.Item label="Updated At">{new Date(selectedEmail.updatedAt).toLocaleString()}</Descriptions.Item>
                            <Descriptions.Item label="Available At">{new Date(selectedEmail.availableAt).toLocaleString()}</Descriptions.Item>
                            <Descriptions.Item label="Attempts">{selectedEmail.attemptCount} / {selectedEmail.maxAttempts}</Descriptions.Item>
                            <Descriptions.Item label="Idempotency Key" span={2}>{selectedEmail.idempotencyKey}</Descriptions.Item>
                            {selectedEmail.providerMessageId && (
                                <Descriptions.Item label="Provider Message ID" span={2}>
                                    {selectedEmail.providerMessageId}
                                </Descriptions.Item>
                            )}
                            {selectedEmail.lastErrorCode && (
                                <>
                                    <Descriptions.Item label="Last Error Code" span={2}>
                                        <Tag color="red">{selectedEmail.lastErrorCode}</Tag>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Last Error Message" span={2}>
                                        <div style={{ color: 'red', whiteSpace: 'pre-wrap' }}>
                                            {selectedEmail.lastErrorMessage}
                                        </div>
                                    </Descriptions.Item>
                                </>
                            )}
                        </Descriptions>

                        {/* Email Body Preview */}
                        {selectedEmail.bodyHtml && (
                            <div style={{ marginTop: 16 }}>
                                <h4>Email Body (HTML):</h4>
                                <div
                                    style={{
                                        border: '1px solid #d9d9d9',
                                        padding: 16,
                                        maxHeight: 400,
                                        overflow: 'auto',
                                        backgroundColor: '#f5f5f5'
                                    }}
                                    dangerouslySetInnerHTML={{ __html: selectedEmail.bodyHtml }}
                                />
                            </div>
                        )}

                        {selectedEmail.bodyText && !selectedEmail.bodyHtml && (
                            <div style={{ marginTop: 16 }}>
                                <h4>Email Body (Text):</h4>
                                <pre style={{ border: '1px solid #d9d9d9', padding: 16, backgroundColor: '#f5f5f5' }}>
                                    {selectedEmail.bodyText}
                                </pre>
                            </div>
                        )}
                    </>
                )}
            </Modal>
        </AccessControl>
    );
};

export default EmailQueue;
